import { Client } from "@stomp/stompjs";
import { Controls3cPlusActions } from "contexts/types/context-3cplus-types";
import { Dispatch } from "react";

type StompCallback = (
  message: any,
  ctx: { dispatch3cPlus: Dispatch<Controls3cPlusActions> }
) => void;

export class StompManager {
  private client: Client | null = null;
  // map of eventType => callback
  private topicsMap: Map<string, StompCallback> = new Map();
  // map of eventType => subscription
  private subs: Map<string, any> = new Map();
  private activating = false;

  init(connectHeaders: any, brokerURL: string, onConnectHook?: () => void) {
    if (this.client) {
      // already created, ensure headers updated if necessary
      return;
    }
    if (this.activating) return;
    this.activating = true;

    this.client = new Client({
      connectHeaders,
      brokerURL,
      debug: () => {},
      reconnectDelay: 500,
      onConnect: () => {
        // unsubscribe existing subscription objects just in case
        this.unsubscribeAllInternal();
        // (re)subscribe to all topics registered in topicsMap
        this.topicsMap.forEach((cb, eventType) => {
          try {
            const sub = this.client!.subscribe(eventType, (message) => {
              cb(message, { dispatch3cPlus: undefined as any }); // placeholder; will be replaced below when subscribe is called with dispatch context
            });
            if (sub) this.subs.set(eventType, sub);
          } catch (e) {
            // ignore individual subscribe errors
          }
        });
        this.activating = false;
        onConnectHook?.();
      },
    });

    this.client.activate();
  }

  // Register desired topic + callback. Provide dispatch context at registration time.
  registerTopic(
    eventType: string,
    callback: StompCallback,
    dispatch3cPlus: Dispatch<Controls3cPlusActions>
  ) {
    // store desired callback wrapped to include the dispatch passed now
    const wrappedCb: StompCallback = (message, ctx) =>
      callback(message, { dispatch3cPlus });
    this.topicsMap.set(eventType, wrappedCb);

    // if client is connected we (re)subscribe immediately ensuring we don't have duplicate sub
    if (this.client && this.client.connected) {
      // unsubscribe existing sub for this event if present
      const existing = this.subs.get(eventType);
      try {
        existing?.unsubscribe?.();
      } catch (e) {}
      try {
        const sub = this.client.subscribe(eventType, (message) => {
          wrappedCb(message, { dispatch3cPlus });
        });
        if (sub) this.subs.set(eventType, sub);
      } catch (e) {
        // ignore
      }
    }
  }

  unsubscribeAllInternal() {
    this.subs.forEach((s) => {
      try {
        s.unsubscribe?.();
      } catch (e) {}
    });
    this.subs.clear();
  }

  // public cleanup
  unsubscribeAll() {
    this.unsubscribeAllInternal();
    this.topicsMap.clear();
  }

  disconnect() {
    this.unsubscribeAllInternal();
    this.client?.deactivate();
    this.client = null;
    this.activating = false;
  }
}

export const stompManager = new StompManager();
